/* eslint-disable react/prop-types */
import React, { useRef, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import Box from "@material-ui/core/Box";
import {
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  LinearProgress,
  IconButton,
} from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import { useMutation } from "@apollo/client";
import { toast } from "react-toastify";

import {
  START_COURSE_UPLOAD,
  GET_COURSE_UPLOAD_URLS,
  COMPLETE_COURSE_UPLOAD,
  APPEND_COURSE_VIDEO,
  UPLOAD_COURSE_IMAGE,
} from "graphql/mutation/admin";
import { useStyles } from "Components/Modal/ModalStyles";
import { ReactComponent as CloseIcon } from "Assets/Svg/close.svg";
import {
  uploadCourseVideo,
  getVideoDuration,
  uploadCourseImageFile,
} from "../utils/uploadCourseVideo";

const stripExtension = (name) => name.replace(/\.[^/.]+$/, "");
const isValidUrl = (value) => {
  if (!value) return true;
  try {
    // eslint-disable-next-line no-new
    new URL(value);
    return true;
  } catch {
    return false;
  }
};

const TITLE_MAX = 200; // AppendCourseVideoInput.title @constraint(maxLength: 200)
const DESC_MAX = 1000; // AppendCourseVideoInput.description @constraint(maxLength: 1000)

const AddVideoModal = ({
  open,
  handleClose,
  courseId,
  handleOnSuccess,
  existingTitles = [],
}) => {
  const classes = useStyles();
  const idRef = useRef(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [startCourseUpload] = useMutation(START_COURSE_UPLOAD);
  const [getCourseUploadUrls] = useMutation(GET_COURSE_UPLOAD_URLS);
  const [completeCourseUpload] = useMutation(COMPLETE_COURSE_UPLOAD);
  const [appendCourseVideo] = useMutation(APPEND_COURSE_VIDEO);
  const [uploadCourseImage] = useMutation(UPLOAD_COURSE_IMAGE);

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []).filter((f) =>
      f.type?.startsWith("video/")
    );
    if (files.length === 0) {
      toast.error("Only video files are allowed");
      return;
    }
    const newRows = files.map((file) => {
      idRef.current += 1;
      return {
        id: idRef.current,
        file,
        title: stripExtension(file.name),
        description: "",
        thumbnailUrl: "",
        thumbUploading: false,
        status: "idle", // idle | uploading | done | error
        progress: 0,
        error: "",
      };
    });
    setRows((prev) => [...prev, ...newRows]);
  };

  const updateRow = (id, field, value) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r))
    );
  };

  const removeRow = (id) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const handleThumbnailSelect = async (rowId, file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("Only image files are allowed for thumbnails");
      return;
    }
    updateRow(rowId, "thumbUploading", true);
    try {
      const { fileUrl } = await uploadCourseImageFile({
        file,
        courseId,
        uploadCourseImage,
      });
      updateRow(rowId, "thumbnailUrl", fileUrl);
    } catch (err) {
      toast.error(err.message || "Failed to upload thumbnail");
    } finally {
      updateRow(rowId, "thumbUploading", false);
    }
  };

  // Per-row + cross-batch validation, mirroring AppendCourseVideoInput constraints
  const existingSet = new Set(
    (existingTitles || []).map((t) => t.trim().toLowerCase())
  );

  const titleError = (row, index) => {
    const t = row.title.trim();
    if (!t) return "Title is required";
    if (t.length > TITLE_MAX) return `Title must be at most ${TITLE_MAX} characters`;
    if (existingSet.has(t.toLowerCase())) {
      return "A video with this title already exists in this course";
    }
    const dupInBatch = rows.some(
      (o, i) => i !== index && o.title.trim().toLowerCase() === t.toLowerCase()
    );
    if (dupInBatch) return "Duplicate title in this batch";
    return "";
  };
  const descError = (row) =>
    row.description.length > DESC_MAX
      ? `Description must be at most ${DESC_MAX} characters`
      : "";
  const thumbError = (row) =>
    isValidUrl(row.thumbnailUrl.trim()) ? "" : "Enter a valid URL";

  const canUpload =
    !loading &&
    rows.length > 0 &&
    !rows.some((r) => r.thumbUploading) &&
    rows.every(
      (r, i) => !titleError(r, i) && !descError(r) && !thumbError(r)
    );

  const uploadRow = async (row) => {
    updateRow(row.id, "status", "uploading");
    updateRow(row.id, "progress", 0);
    updateRow(row.id, "error", "");
    try {
      const duration = await getVideoDuration(row.file);
      const { fileUrl } = await uploadCourseVideo({
        file: row.file,
        startCourseUpload,
        getCourseUploadUrls,
        completeCourseUpload,
        onProgress: (p) => updateRow(row.id, "progress", p),
      });

      const { data } = await appendCourseVideo({
        variables: {
          input: {
            courseId,
            title: row.title.trim(),
            description: row.description?.trim() || undefined,
            mediaUrl: fileUrl,
            thumbnailUrl: row.thumbnailUrl?.trim() || undefined,
            duration: duration || undefined,
          },
        },
      });

      const { status, message } = data.appendCourseVideo;
      if (status === "success") {
        updateRow(row.id, "status", "done");
        return true;
      }
      updateRow(row.id, "status", "error");
      updateRow(row.id, "error", message || "Failed to add video");
      return false;
    } catch (err) {
      updateRow(row.id, "status", "error");
      updateRow(row.id, "error", err.message || "Upload failed");
      return false;
    }
  };

  const handleUploadAll = async () => {
    if (!canUpload) return;
    setLoading(true);
    // Process every row that hasn't already succeeded (covers retries of errors).
    const pending = rows.filter((r) => r.status !== "done");
    let success = 0;
    for (let i = 0; i < pending.length; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      const ok = await uploadRow(pending[i]);
      if (ok) success += 1;
    }
    setLoading(false);

    if (success > 0) {
      handleOnSuccess();
      toast.success(`${success} video${success > 1 ? "s" : ""} added`);
    }
    if (success === pending.length) {
      handleClose();
    } else {
      toast.error("Some videos failed to upload — review the highlighted rows");
    }
  };

  const renderStatus = (row) => {
    if (row.status === "uploading") {
      return (
        <Box style={{ marginTop: 8 }}>
          <LinearProgress variant="determinate" value={row.progress} />
          <Typography variant="caption" style={{ color: "#888" }}>
            Uploading… {row.progress}%
          </Typography>
        </Box>
      );
    }
    if (row.status === "done") {
      return (
        <Box style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, color: "#2e7d32" }}>
          <CheckCircleIcon style={{ fontSize: 18 }} />
          <Typography variant="caption">Uploaded</Typography>
        </Box>
      );
    }
    if (row.status === "error") {
      return (
        <Box style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, color: "#c62828" }}>
          <ErrorOutlineIcon style={{ fontSize: 18 }} />
          <Typography variant="caption">{row.error}</Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Dialog
      open={open}
      aria-labelledby="add-video-title"
      maxWidth="sm"
      onClose={() => {
        if (!loading) handleClose();
      }}
      PaperProps={{
        style: {
          borderRadius: 15,
          width: 560,
          maxWidth: "95vw",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogContent
        classes={{ root: classes.dialogContext }}
        style={{
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          minHeight: 0,
        }}
      >
        {/* Fixed header */}
        <div
          role="button"
          tabIndex={0}
          onKeyDown={loading ? undefined : handleClose}
          onClick={loading ? undefined : handleClose}
          className={classes.topBar}
          style={{ flexShrink: 0 }}
        >
          <span className={classes.title}>Add Videos</span>
          <span className={`${classes.onHover} ${classes.closeBtn}`}>
            <CloseIcon />
          </span>
        </div>

        {/* Scrollable body */}
        <Box style={{ flex: 1, overflowY: "auto", minHeight: 0, padding: "16px 24px" }}>
          <Button variant="outlined" component="label" fullWidth disabled={loading}>
            Select Video File(s)
            <input
              type="file"
              accept="video/*"
              multiple
              hidden
              onChange={(e) => {
                addFiles(e.target.files);
                // reset so the same file can be picked again later
                e.target.value = "";
              }}
            />
          </Button>

          {rows.length === 0 && (
            <Typography
              variant="body2"
              style={{ color: "#888", textAlign: "center", marginTop: 24 }}
            >
              No videos selected yet. Pick one or more video files to upload.
            </Typography>
          )}

          {rows.map((row, index) => (
            <Box
              key={row.id}
              style={{
                border: `1px solid ${row.status === "error" ? "#e57373" : "#e0e0e0"}`,
                borderRadius: 8,
                padding: 12,
                marginTop: 16,
                background: row.status === "done" ? "#f1f8f2" : "#fff",
              }}
            >
              <Box style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <Typography
                  variant="caption"
                  style={{ color: "#888", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  title={row.file.name}
                >
                  {index + 1}. {row.file.name}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => removeRow(row.id)}
                  disabled={loading}
                  title="Remove"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Title"
                    variant="outlined"
                    size="small"
                    fullWidth
                    autoComplete="off"
                    value={row.title}
                    onChange={(e) => updateRow(row.id, "title", e.target.value)}
                    disabled={loading}
                    error={Boolean(titleError(row, index))}
                    helperText={
                      titleError(row, index) || "Edit the title before uploading"
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Description (optional)"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={row.description}
                    onChange={(e) => updateRow(row.id, "description", e.target.value)}
                    disabled={loading}
                    error={Boolean(descError(row))}
                    helperText={descError(row)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                    {row.thumbnailUrl && isValidUrl(row.thumbnailUrl.trim()) && (
                      <img
                        src={row.thumbnailUrl}
                        alt="thumbnail"
                        style={{
                          width: 56,
                          height: 56,
                          objectFit: "cover",
                          borderRadius: 6,
                          border: "1px solid #e0e0e0",
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <TextField
                      label="Thumbnail URL (optional)"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={row.thumbnailUrl}
                      onChange={(e) => updateRow(row.id, "thumbnailUrl", e.target.value)}
                      disabled={loading || row.thumbUploading}
                      error={Boolean(thumbError(row))}
                      helperText={thumbError(row) || "Paste a URL or upload an image"}
                    />
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      disabled={loading || row.thumbUploading}
                      style={{ whiteSpace: "nowrap", height: 40 }}
                    >
                      {row.thumbUploading ? (
                        <CircularProgress size={18} />
                      ) : (
                        "Upload"
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(e) => {
                          handleThumbnailSelect(row.id, e.target.files?.[0]);
                          e.target.value = "";
                        }}
                      />
                    </Button>
                  </Box>
                </Grid>
              </Grid>

              {renderStatus(row)}
            </Box>
          ))}

        </Box>

        {/* Fixed footer */}
        <Box
          style={{
            flexShrink: 0,
            borderTop: "1px solid #00000014",
            display: "flex",
            justifyContent: "space-evenly",
            padding: "16px 30px",
          }}
        >
          <Button
            variant="contained"
            size="small"
            style={{
              width: "155px",
              height: "40px",
              fontWeight: "550",
              color: "black",
              boxShadow: "none",
              fontSize: "14px",
            }}
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleUploadAll}
            style={{
              minWidth: "155px",
              height: "40px",
              fontWeight: "550",
              boxShadow: "none",
              fontSize: "14px",
              background: `var(--color-primary)`,
              color: "white",
              opacity: canUpload ? 1 : 0.6,
            }}
            disabled={!canUpload}
          >
            {loading && (
              <CircularProgress
                style={{ width: "20px", height: "20px", color: "#fff", marginRight: "10px" }}
              />
            )}
            {loading
              ? "Uploading..."
              : `Upload${rows.length ? ` ${rows.length} Video${rows.length > 1 ? "s" : ""}` : ""}`}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AddVideoModal;
